import type { CompletionFields, CompletionTarget, PhotoReceipt } from "@/lib/completionDraftApi";

export type CompletionPhotoState = "selected" | "preparing" | "ready" | "uploading" | "uploaded" | "failed" | "missing";
export interface StoredCompletionPhoto {
  id: string;
  name: string;
  sourceBytes: number;
  preparedBytes?: number;
  state: CompletionPhotoState;
  normalize?: boolean;
  checksum?: string;
  receipt?: PhotoReceipt;
  error?: string;
}
export interface PendingFinalization {
  key: string;
  photoIds: string[];
  fields: CompletionFields;
}
export interface StoredCompletionDraft {
  key: string;
  ownerId: string;
  id: string;
  target: CompletionTarget;
  remoteCreated: boolean;
  mode: "draft" | "legacy" | null;
  photos: StoredCompletionPhoto[];
  removedIds: string[];
  fields: CompletionFields;
  pending?: PendingFinalization;
  updatedAt: number;
}

let database: Promise<IDBDatabase> | undefined;
function openDatabase(): Promise<IDBDatabase> {
  if (!database) {
    database = new Promise((resolve, reject) => {
      const request = indexedDB.open("chauvin-completion-drafts", 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore("drafts", { keyPath: "key" });
        request.result.createObjectStore("photos", { keyPath: "key" });
      };
      request.onsuccess = () => {
        request.result.onversionchange = () => { request.result.close(); database = undefined; };
        resolve(request.result);
      };
      request.onerror = () => { database = undefined; reject(request.error); };
    });
    void database.catch(() => { database = undefined; });
  }
  return database;
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  const done = new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = tx.onabort = () => reject(tx.error ?? new Error("Sauvegarde locale indisponible."));
  });
  // A failed individual request may reject before its transaction does.
  // Keep that second rejection handled even when the caller exits early.
  void done.catch(() => undefined);
  return done;
}
function result<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export const completionDraftStorage = {
  async read(key: string): Promise<{ draft?: StoredCompletionDraft; blobs: Map<string, Blob> }> {
    const db = await openDatabase();
    const tx = db.transaction(["drafts", "photos"], "readonly");
    const done = transactionDone(tx);
    const draftRequest = tx.objectStore("drafts").get(key);
    const photosRequest = tx.objectStore("photos").getAll(IDBKeyRange.bound(`${key}:`, `${key}:\uffff`));
    const [draft, records] = await Promise.all([
      result<StoredCompletionDraft | undefined>(draftRequest),
      result<{ key: string; blob: Blob }[]>(photosRequest),
    ]);
    const blobs = new Map<string, Blob>();
    for (const record of records) blobs.set(record.key.slice(key.length + 1), record.blob);
    await done;
    return { draft, blobs };
  },
  async write(draft: StoredCompletionDraft, prepared?: { id: string; blob: Blob }): Promise<void> {
    const db = await openDatabase();
    const tx = db.transaction(["drafts", "photos"], "readwrite");
    const done = transactionDone(tx);
    tx.objectStore("drafts").put(draft);
    if (prepared) tx.objectStore("photos").put({ key: `${draft.key}:${prepared.id}`, ownerId: draft.ownerId, blob: prepared.blob });
    await done;
  },
  async remove(key: string): Promise<void> {
    const db = await openDatabase();
    const tx = db.transaction(["drafts", "photos"], "readwrite");
    const done = transactionDone(tx);
    tx.objectStore("drafts").delete(key);
    const cursor = tx.objectStore("photos").openCursor(IDBKeyRange.bound(`${key}:`, `${key}:\uffff`));
    cursor.onsuccess = () => {
      if (cursor.result) { cursor.result.delete(); cursor.result.continue(); }
    };
    await done;
  },
  async clearOwner(ownerId: string): Promise<void> {
    const db = await openDatabase();
    const tx = db.transaction(["drafts", "photos"], "readwrite");
    const done = transactionDone(tx);
    for (const name of ["drafts", "photos"]) {
      const cursor = tx.objectStore(name).openCursor();
      cursor.onsuccess = () => {
        if (cursor.result) {
          if (cursor.result.value.ownerId === ownerId) cursor.result.delete();
          cursor.result.continue();
        }
      };
    }
    await done;
  },
};
